import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import PipelineCard from '../../components/PipelineCard';
import { getPipelines } from '../../api/client';
import { useWebSocket } from '../../api/websocket';

// Status priority for sorting (lower number = higher priority, shown first)
const STATUS_PRIORITY = {
  running: 1,
  starting: 2,
  stopping: 3,
  scheduled: 4,
  error: 5,
  idle: 6,
};

const sortPipelinesByStatus = (pipelines) => {
  return [...pipelines].sort((a, b) => {
    const priorityA = STATUS_PRIORITY[a.status] || 99;
    const priorityB = STATUS_PRIORITY[b.status] || 99;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    // Secondary sort by name for pipelines with same status
    return a.name.localeCompare(b.name);
  });
};

function Dashboard() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Connect to WebSocket for real-time updates
  const { status, lastMessage } = useWebSocket('dashboard');

  useEffect(() => {
    fetchPipelines();
  }, []);

  useEffect(() => {
    // Handle WebSocket messages for real-time updates
    if (lastMessage) {
      const data = JSON.parse(lastMessage);
      if (data.type === 'pipeline_status') {
        updatePipelineStatus(data.pipeline_id, data.status, data.metrics);
      }
    }
  }, [lastMessage]);

  const fetchPipelines = async () => {
    try {
      setLoading(true);
      const data = await getPipelines();
      setPipelines(data);
      setError(null);
    } catch (err) {
      setError('Failed to load pipelines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updatePipelineStatus = (pipelineId, status, metrics) => {
    setPipelines((prev) =>
      prev.map((p) =>
        p.id === pipelineId
          ? { ...p, status, metrics: { ...p.metrics, ...metrics } }
          : p
      )
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Pipeline Dashboard
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6">Active Pipelines</Typography>
              <Typography variant="h3">
                {pipelines.filter(p => p.status === 'running').length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6">Total Pipelines</Typography>
              <Typography variant="h3">{pipelines.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6">Estimated Cost/hr</Typography>
              <Typography variant="h3">
                ${pipelines
                  .filter(p => p.status === 'running')
                  .reduce((sum, p) => sum + (p.estimated_hourly_cost || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {pipelines.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No pipelines configured
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Go to Admin panel to create your first pipeline
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sortPipelinesByStatus(pipelines).map((pipeline) => (
            <Grid item xs={12} md={6} lg={4} key={pipeline.id}>
              <PipelineCard pipeline={pipeline} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default Dashboard;